import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthLayout, Button, Card, ErrorMessage, Field, Input, PasswordInput } from '../components/ui.tsx';
import { ApiError, invitationsApi } from '../lib/api.ts';
import { authClient } from '../lib/auth-client.ts';

/** Mínimo que exige el servidor para una contraseña. */
const MIN_PASSWORD = 12;

/**
 * Aceptar una invitación: la puerta de entrada al panel.
 *
 * Con el registro cerrado, esta es la **única** forma de crear una cuenta. Quien
 * llega viene de un correo y casi siempre sin cuenta todavía, así que la
 * pantalla no le pide que se registre en otro sitio y vuelva: le dice a qué le
 * invitaron y le deja elegir su nombre y su contraseña aquí mismo.
 *
 * El correo no se puede cambiar. La invitación es para una dirección concreta,
 * y el servidor solo deja crear la cuenta de esa dirección a quien presenta el
 * identificador de la invitación, que únicamente viaja en ese correo. Eso es lo
 * que impide que alguien que conozca a quién se invitó se le adelante.
 */
export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invitationId } = useParams({ from: '/aceptar-invitacion/$invitationId' });

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sessionEmail, setSessionEmail] = useState<string | null | undefined>(undefined);

  const preview = useQuery({
    queryKey: ['invitation', invitationId],
    queryFn: () => invitationsApi.preview(invitationId),
    retry: false,
  });

  useEffect(() => {
    void (async () => {
      const { data } = await authClient.getSession();
      setSessionEmail(data?.user.email ?? null);
    })();
  }, []);

  /** Acepta con la sesión que haya y entra directamente en la organización. */
  async function accept() {
    const { data, error: acceptError } = await authClient.organization.acceptInvitation({ invitationId });

    if (acceptError || !data) {
      setBusy(false);
      setError(acceptError?.message ?? t('invitation.failed'));
      return;
    }

    // Sin esto, quien acaba de entrar aterriza en "elige una organización" con
    // una sola opción: un clic que no decide nada.
    await authClient.organization.setActive({ organizationId: data.invitation.organizationId });
    await queryClient.invalidateQueries();
    await navigate({ to: '/' });
  }

  async function createAccountAndAccept(event: React.FormEvent) {
    event.preventDefault();

    if (!preview.data) {
      return;
    }

    if (password.length < MIN_PASSWORD) {
      setError(t('auth.reset.tooShort', { min: MIN_PASSWORD }));
      return;
    }

    setError(null);
    setBusy(true);

    const { error: signUpError } = await authClient.signUp.email(
      { name: name.trim(), email: preview.data.email, password },
      // El identificador de la invitación va en una cabecera: es lo que el
      // servidor exige para dejar crear esta cuenta.
      { headers: { 'X-Invitation-Id': invitationId } },
    );

    if (signUpError) {
      setBusy(false);
      // 422: esa dirección ya tiene cuenta. No hay que crear otra, hay que entrar.
      setError(
        signUpError.status === 422
          ? t('invitation.alreadyHasAccount')
          : (signUpError.message ?? t('invitation.failed')),
      );
      return;
    }

    await accept();
  }

  const invalid = preview.error instanceof ApiError && preview.error.status === 404;
  const loading = preview.isLoading || sessionEmail === undefined;
  const invitation = preview.data;

  // Hay sesión, pero de otra persona: aceptar fallaría con un mensaje críptico.
  const wrongAccount =
    Boolean(invitation && sessionEmail) && sessionEmail?.toLowerCase() !== invitation?.email.toLowerCase();

  return (
    <AuthLayout>
      <Card title={t('invitation.title')}>
        {loading ? <p className="text-sm text-[var(--color-muted)]">{t('common.loading')}</p> : null}

        {!loading && (invalid || preview.isError) ? (
          <>
            <ErrorMessage>{invalid ? t('invitation.expired') : t('errors.network')}</ErrorMessage>
            <p className="text-sm leading-relaxed text-[var(--color-muted)]">{t('invitation.expiredHelp')}</p>
          </>
        ) : null}

        {!loading && invitation ? (
          <>
            <p className="mb-5 text-sm leading-relaxed text-[var(--color-muted)]">
              {t('invitation.intro')}{' '}
              <strong className="font-medium text-[var(--color-ink)]">{invitation.organizationName}</strong>.
            </p>

            <ErrorMessage>{error}</ErrorMessage>

            {wrongAccount ? (
              <>
                <ErrorMessage>
                  {t('invitation.wrongAccount', { invited: invitation.email, current: sessionEmail })}
                </ErrorMessage>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={async () => {
                    await authClient.signOut();
                    setSessionEmail(null);
                  }}
                >
                  {t('common.signOut')}
                </Button>
              </>
            ) : sessionEmail ? (
              <Button
                type="button"
                loading={busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  void accept();
                }}
              >
                {busy ? t('invitation.accepting') : t('invitation.accept')}
              </Button>
            ) : (
              <form onSubmit={createAccountAndAccept} noValidate>
                <Field id="email" label={t('common.email')} hint={t('invitation.emailHint')}>
                  <Input id="email" type="email" value={invitation.email} readOnly disabled />
                </Field>

                <Field id="name" label={t('common.name')}>
                  <Input
                    id="name"
                    autoComplete="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>

                <Field id="password" label={t('common.password')} hint={t('auth.signUp.passwordHint')}>
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD}
                    required
                    aria-describedby="password-hint"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>

                <Button type="submit" loading={busy} disabled={name.trim() === '' || password === ''}>
                  {busy ? t('invitation.accepting') : t('invitation.createAndEnter')}
                </Button>

                <p className="mt-5 text-center text-sm text-[var(--color-muted)]">
                  {t('auth.signUp.haveAccount')}{' '}
                  <Link
                    to="/entrar"
                    search={{ redirect: `/aceptar-invitacion/${invitationId}` }}
                    className="text-[var(--color-brand)] hover:underline"
                  >
                    {t('auth.signUp.signIn')}
                  </Link>
                </p>
              </form>
            )}
          </>
        ) : null}
      </Card>
    </AuthLayout>
  );
}
